/**
 * 현장 페이지 공통 셸 — 헤더(브레드크럼·테마·설정·대시보드·계정) + 좌측 DashboardSidebar + main.
 *
 * 사이드바가 사라져 보이는 문제를 막기 위해 자재/히스토리처럼 별도 라우트로 분리된 페이지도
 * 이 셸 안에 children 으로 들어가게 해서 동일한 좌측 패널을 유지한다.
 *
 * 사이드바 클릭 라우팅 규칙(한 곳에서만 정의):
 *   - 'materials'         → `/project/:id/materials`
 *   - 'qa'                → `/project/:id/qa`
 *   - 'history'           → `/audit-logs?project=:id`
 *   - 그 외 (overview/deploy/schedules/photos/network)
 *                         → `/project/:id?section=:section`  (ProjectDashboardPage 가 쿼리에서 읽음)
 *
 * ProjectDashboardPage 자체도 같은 사이드바를 갖고 있으므로 셸을 강제하지 않으며, 진입 시
 * URL 쿼리(?section)만 읽으면 충돌 없이 동기화된다.
 */
import React, { useContext } from 'react';
import {
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  Chip,
  IconButton,
  Button,
  Container,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Home as HomeIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  Settings as SettingsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import UserProfileMenu from '../../components/UserProfileMenu';
import DashboardSidebar, { DashboardSection, MobileSectionNav } from './DashboardSidebar';

interface StoredProject {
  id?: string | number;
  name?: string;
  location?: string;
  status?: string;
  dashboard_url?: string;
  construction_completed_at?: string | null;
  final_completed_at?: string | null;
}

export interface ProjectPageShellProps {
  /** URL 의 projectId — string 또는 number 모두 허용. */
  projectId: number | string;
  /** 현재 활성 섹션. 사이드바에서 굵게 표시. */
  activeSection: DashboardSection;
  /**
   * 추가 브레드크럼 항목 — 예: 자재 페이지는 "현장 > 오송 > 자재 관리".
   * `<Typography>` 또는 `<Link>` 등 BreadcrumbItem 류 노드를 넘긴다.
   */
  breadcrumbExtra?: React.ReactNode;
  /**
   * 헤더 우측의 "현장 설정" 버튼을 숨길지. 자재/히스토리 페이지에선 굳이 노출 안 함.
   * 기본은 표시(false).
   */
  hideSettingsButton?: boolean;
  /** main 영역의 가로 폭 제약. 기본은 'lg' — AuditLog 등 좁은 본문 그대로. */
  contentMaxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false;
  children: React.ReactNode;
}

function readStoredProject(): StoredProject | null {
  const raw = localStorage.getItem('selectedProject');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredProject;
  } catch {
    return null;
  }
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('ko-KR');
  } catch {
    return iso;
  }
}

const ProjectPageShell: React.FC<ProjectPageShellProps> = ({
  projectId,
  activeSection,
  breadcrumbExtra,
  hideSettingsButton = false,
  contentMaxWidth = 'lg',
  children,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const projectData = readStoredProject();
  const projectIdStr = String(projectId);

  const constructionDate = formatDate(projectData?.construction_completed_at);
  const finalDate = formatDate(projectData?.final_completed_at);

  // 사이드바 클릭 → 페이지 라우팅. ProjectDashboardPage 의 내부 section state 와
  // 동기화하려면 ?section=xxx 쿼리로 넘긴다 (ProjectDashboardPage 가 useSearchParams 로 읽음).
  const handleSidebarChange = (section: DashboardSection): void => {
    if (section === 'materials') {
      navigate(`/project/${projectIdStr}/materials`);
      return;
    }
    if (section === 'documents') {
      navigate(`/project/${projectIdStr}/documents`);
      return;
    }
    if (section === 'qa') {
      navigate(`/project/${projectIdStr}/qa`);
      return;
    }
    if (section === 'history') {
      navigate(`/audit-logs?project=${projectIdStr}`);
      return;
    }
    navigate(`/project/${projectIdStr}?section=${section}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* 모바일에서는 좌측(브레드크럼)·우측(버튼) 그룹이 줄바꿈되도록 flexWrap.
          nowrap 없이 flex 가 줄어들면 글자가 세로로 깨진다 (ProjectDashboardPage 헤더와 동일 규칙). */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, md: 4 },
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate(`/project/${projectIdStr}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontWeight: breadcrumbExtra ? 500 : 700,
                color: breadcrumbExtra ? 'inherit' : 'text.primary',
              }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {projectData?.name || `프로젝트 ${projectIdStr}`}
            </Link>
            {breadcrumbExtra}
          </Breadcrumbs>
          {projectData?.location && !breadcrumbExtra && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {projectData.location}
            </Typography>
          )}
          {projectData?.status && !breadcrumbExtra && (
            <Chip
              size="small"
              label={
                projectData.status === 'active'
                  ? '● 운영중'
                  : projectData.status === 'maintenance'
                    ? '◐ 점검중'
                    : '○ 비활성'
              }
              color={projectData.status === 'active' ? 'success' : 'default'}
              variant="outlined"
            />
          )}
          {!breadcrumbExtra && (constructionDate || finalDate) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
              {constructionDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="준공일">
                  <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    준공
                  </Typography>
                  <Typography variant="caption" color="text.primary" fontWeight={600}>
                    {constructionDate}
                  </Typography>
                </Box>
              )}
              {finalDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="완공일">
                  <EventAvailableIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    완공
                  </Typography>
                  <Typography variant="caption" color="text.primary" fontWeight={600}>
                    {finalDate}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          {!hideSettingsButton && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => navigate(`/project/${projectIdStr}/settings`)}
              sx={{ borderRadius: 2 }}
            >
              현장 설정
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DashboardIcon />}
            onClick={() => {
              if (projectData?.dashboard_url) {
                window.open(projectData.dashboard_url, '_blank', 'noopener,noreferrer');
              }
            }}
            disabled={!projectData?.dashboard_url}
            title={projectData?.dashboard_url ? '대시보드 열기' : '대시보드 URL 미등록'}
            sx={{ borderRadius: 2 }}
          >
            대시보드
          </Button>
          <UserProfileMenu />
        </Box>
      </Paper>

      {/* md 미만: 사이드바가 숨겨지므로 가로 스크롤 탭으로 섹션 전환
          (ProjectDashboardPage 와 동일 — 자재/파일/히스토리 페이지도 모바일에서
          내비게이션을 잃지 않는다). */}
      <MobileSectionNav active={activeSection} onChange={handleSidebarChange} />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <DashboardSidebar active={activeSection} onChange={handleSidebarChange} />

        <Box component="main" sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
          {contentMaxWidth === false ? (
            <Box sx={{ mt: 3, mb: 4, px: 3 }}>{children}</Box>
          ) : (
            <Container maxWidth={contentMaxWidth} sx={{ mt: 3, mb: 4 }}>
              {children}
            </Container>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectPageShell;
